/**
 * 角色管理页面
 */

import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Plus,
  Search,
  Trash2,
  Shield,
  Settings,
  X,
  Check,
  Loader2,
} from 'lucide-react';
import styles from './Roles.module.css';
import { api, getErrorMessage } from '@/services/api';
import { useToast } from '@/components/Toast';
import { useConfirm } from '@/components/ConfirmModal';

interface Permission {
  perm_id: number;
  perm_name: string;
  perm_desc: string | null;
}

interface PermissionGroup {
  title: string;
  permissions: Permission[];
}

interface Role {
  role_id: number;
  role_name: string;
  function?: string;
  description: string | null;
  is_system: boolean;
  permissions: string[];
  user_count: number;
  create_time: string;
}

const FUNC_LABELS: Record<string, string> = {
  exam: '学习考试', knowledge: '知识传承', share: '分享平台', work: '日常工作', admin: '管理员', drawing: '图纸管理',
};

// 权限分组配置
const PERMISSION_GROUPS: { title: string; permNames: string[] }[] = [
  {
    title: '管理员功能',
    permNames: ['用户管理', '标签管理', '部门管理', '角色管理', '操作日志'],
  },
  {
    title: '图纸管理功能',
    permNames: ['素材管理', '产品管理', '模板配置'],
  },
  {
    title: '知识传承功能',
    permNames: ['知识管理', '知识问答对话'],
  },
  {
    title: '学习考试功能',
    permNames: ['任务管理', '我的任务'],
  },
  {
    title: '日常工作功能',
    permNames: ['日常工作'],
  },
];

export default function RolesPage() {
  const { showToast } = useToast();
  const confirmDialog = useConfirm();
  const [roles, setRoles] = useState<Role[]>([]);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState('');
  const [sortDir, setSortDir] = useState<'asc'|'desc'>('asc');

  // 分页状态
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const [showModal, setShowModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingRole, setDeletingRole] = useState<Role | null>(null);
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    role_name: '',
    description: '',
    permission_ids: [] as number[],
  });

  // 加载数据
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [rolesRes, permsRes] = await Promise.all([
        api.getRoles(),
        api.getAllPermissions(),
      ]);
      if (rolesRes.code === 200) {
        setRoles(rolesRes.data);
      }
      if (permsRes.code === 200) {
        setPermissions(permsRes.data);
      }
    } catch (error) {
      console.error('加载数据失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field: string) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };
  const SortTh = ({ field, children }: { field: string; children: React.ReactNode }) => (
    <th className={styles.centerCell} style={{cursor:'pointer',userSelect:'none'}} onClick={() => handleSort(field)}>
      {children} {sortField === field ? (sortDir === 'asc' ? '▲' : '▼') : ''}
    </th>
  );

  const filteredRoles = roles.filter((role) => {
    if (role.role_name === '超级管理员') return false;
    return role.role_name.toLowerCase().includes(searchQuery.toLowerCase());
  });

  // 排序
  if (sortField) {
    filteredRoles.sort((a: any, b: any) => {
      let va = a[sortField] ?? '', vb = b[sortField] ?? '';
      if (sortField === 'permissions') { va = (a.permissions || []).length; vb = (b.permissions || []).length; }
      const cmp = typeof va === 'number' ? va - vb : String(va).localeCompare(String(vb));
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }

  // 分页计算
  const totalPages = Math.ceil(filteredRoles.length / itemsPerPage);
  const paginatedRoles = filteredRoles.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleCreate = () => {
    setIsCreating(true);
    setEditingRole(null);
    setFormData({
      role_name: '',
      description: '',
      permission_ids: [],
    });
    setShowModal(true);
  };

  const handleEdit = (role: Role) => {
    setIsCreating(false);
    setEditingRole(role);
    // 将权限名称转换为权限ID
    const permIds = permissions
      .filter(p => role.permissions.includes(p.perm_name))
      .map(p => p.perm_id);
    setFormData({
      role_name: role.role_name,
      description: role.description || '',
      permission_ids: permIds,
    });
    setShowModal(true);
  };

  const handleDeleteClick = (role: Role) => {
    if (role.is_system) {
      showToast('warning', '系统角色无法删除');
      return;
    }
    setDeletingRole(role);
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deletingRole) return;

    try {
      const res = await api.deleteRole(deletingRole.role_id);
      if (res.code === 200) {
        setRoles(roles.filter(r => r.role_id !== deletingRole.role_id));
        setShowDeleteConfirm(false);
        setDeletingRole(null);
        showToast('success', '删除成功');
      } else {
        showToast('error', res.message || '删除失败');
      }
    } catch (error: unknown) {
      showToast('error', getErrorMessage(error) || '删除失败');
    }
  };

  const togglePermission = (permissionId: number) => {
    setFormData((prev) => ({
      ...prev,
      permission_ids: prev.permission_ids.includes(permissionId)
        ? prev.permission_ids.filter((p) => p !== permissionId)
        : [...prev.permission_ids, permissionId],
    }));
  };

  const handleSave = async () => {
    if (!formData.role_name.trim()) {
      showToast('warning', '请输入角色名称');
      return;
    }

    setSaving(true);
    try {
      if (isCreating) {
        // 创建新角色
        const res = await api.createRole({
          role_name: formData.role_name,
          description: formData.description || null,
          permission_ids: formData.permission_ids,
        });
        if (res.code === 200) {
          setRoles([...roles, res.data]);
          setShowModal(false);
          showToast('success', '创建成功');
        } else {
          showToast('error', res.message || '创建失败');
        }
      } else if (editingRole) {
        // 更新角色
        const res = await api.updateRole(editingRole.role_id, {
          role_name: formData.role_name,
          description: formData.description || null,
          permission_ids: formData.permission_ids,
        });
        if (res.code === 200) {
          setRoles(roles.map(r => r.role_id === editingRole.role_id ? res.data : r));
          setShowModal(false);
          showToast('success', '更新成功');
        } else {
          showToast('error', res.message || '更新失败');
        }
      }
    } catch (error: unknown) {
      showToast('error', getErrorMessage(error) || '保存失败');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('zh-CN');
  };

  // 将权限按分组归类
  const getGroupedPermissions = (): PermissionGroup[] => {
    return PERMISSION_GROUPS.map(group => ({
      title: group.title,
      permissions: permissions.filter(p => group.permNames.includes(p.perm_name)),
    })).filter(g => g.permissions.length > 0);
  };

  const groupedPermissions = getGroupedPermissions();

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>
          <Loader2 className={styles.spin} size={32} />
          <span>加载中...</span>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* 页头 */}
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <h1 className={styles.title}>角色管理</h1>
          <p className={styles.subtitle}>管理系统角色和权限配置</p>
        </div>
      </div>

      {/* 搜索栏 */}
      <div className={styles.toolbar}>
        <div className={styles.searchInput}>
          <Search size={18} strokeWidth={1.5} />
          <input
            type="text"
            placeholder="搜索角色名称..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <div className={styles.stats}>
          <span>共 <strong>{roles.length}</strong> 个角色</span>
        </div>
      </div>

      {/* 角色列表 */}
      <div className={styles.tableContainer}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>角色名称</th>
              <th className={styles.centerCell}>归属系统</th>
              <th className={styles.centerCell}>描述</th>
              <SortTh field="permissions">权限数量</SortTh>
              <SortTh field="user_count">用户数</SortTh>
              <SortTh field="create_time">创建时间</SortTh>
              <th className={styles.centerCell}>操作</th>
            </tr>
          </thead>
          <tbody>
            {paginatedRoles.map((role) => (
              <motion.tr
                key={role.role_id}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
              >
                <td>
                  <div className={styles.roleInfo}>
                    <div className={styles.roleIcon}>
                      <Shield size={16} />
                    </div>
                    <div>
                      <span className={styles.roleName}>{role.role_name}</span>
                      {role.is_system && (
                        <span className={styles.systemTag}>系统角色</span>
                      )}
                    </div>
                  </div>
                </td>
                <td className={styles.centerCell}>
                  <span style={{fontSize:'.8125rem',color:'var(--ink-600)'}}>
                    {FUNC_LABELS[role.function || 'exam'] || role.function || '学习考试'}
                  </span>
                </td>
                <td className={styles.centerCell}>{role.description || '-'}</td>
                <td className={styles.centerCell}>
                  <span className={styles.permissionCount}>
                    {role.permissions.length} 项权限
                  </span>
                </td>
                <td className={styles.centerCell}>{role.user_count} 人</td>
                <td className={styles.centerCell}>{formatDate(role.create_time)}</td>
                <td className={styles.centerCell}>
                  <span style={{fontSize:'.75rem',color:'#9ca3af'}}>预设</span>
                </td>
              </motion.tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* 翻页控件 */}
      <div className={styles.pagination}>
        <div className={styles.paginationInfo}>
          共 {filteredRoles.length} 条记录
        </div>
        <div className={styles.paginationControls}>
          <button
            className={`${styles.paginationBtn} ${currentPage === 1 ? styles.disabled : ''}`}
            onClick={() => setCurrentPage(1)}
            disabled={currentPage === 1}
          >
            首页
          </button>
          <button
            className={`${styles.paginationBtn} ${currentPage === 1 ? styles.disabled : ''}`}
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
          >
            上一页
          </button>
          <div className={styles.pageNumbers}>
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }
              return (
                <button
                  key={pageNum}
                  className={`${styles.pageNumber} ${currentPage === pageNum ? styles.active : ''}`}
                  onClick={() => setCurrentPage(pageNum)}
                >
                  {pageNum}
                </button>
              );
            })}
            {totalPages > 5 && currentPage < totalPages - 2 && (
              <span className={styles.pageEllipsis}>...</span>
            )}
            {totalPages > 5 && currentPage >= totalPages - 2 && (
              <button
                className={`${styles.pageNumber} ${currentPage === totalPages ? styles.active : ''}`}
                onClick={() => setCurrentPage(totalPages)}
              >
                {totalPages}
              </button>
            )}
          </div>
          <button
            className={`${styles.paginationBtn} ${currentPage === totalPages ? styles.disabled : ''}`}
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages}
          >
            下一页
          </button>
          <button
            className={`${styles.paginationBtn} ${currentPage === totalPages ? styles.disabled : ''}`}
            onClick={() => setCurrentPage(totalPages)}
            disabled={currentPage === totalPages}
          >
            末页
          </button>
        </div>
      </div>

      {/* 权限配置弹窗 */}
      <AnimatePresence>
        {showModal && (editingRole || isCreating) && (
          <motion.div
            className={styles.modalOverlay}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowModal(false)}
          >
            <motion.div
              className={styles.modal}
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className={styles.modalHeader}>
                <div className={styles.modalTitleGroup}>
                  <Shield size={20} />
                  <h2>{isCreating ? '新增角色' : `配置「${editingRole?.role_name}」权限`}</h2>
                </div>
                <button
                  className={styles.modalClose}
                  onClick={() => setShowModal(false)}
                >
                  <X size={20} />
                </button>
              </div>

              <div className={styles.modalBody}>
                {/* 角色基本信息 */}
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>角色名称 <span className={styles.required}>*</span></label>
                  <input
                    type="text"
                    className={styles.formInput}
                    value={formData.role_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, role_name: e.target.value }))}
                    placeholder="请输入角色名称"
                    maxLength={50}
                  />
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>描述</label>
                  <input
                    type="text"
                    className={styles.formInput}
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="请输入角色描述"
                    maxLength={200}
                  />
                </div>

                {/* 权限选择 */}
                <div className={styles.permissionSection}>
                  <h3 className={styles.sectionTitle}>权限配置</h3>
                  {groupedPermissions.map((group) => (
                    <div key={group.title} className={styles.permissionGroup}>
                      <h4 className={styles.groupTitle}>{group.title}</h4>
                      <div className={styles.permissionList}>
                        {group.permissions.map((perm) => (
                          <label
                            key={perm.perm_id}
                            className={`${styles.permissionItem} ${
                              formData.permission_ids.includes(perm.perm_id) ? styles.checked : ''
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={formData.permission_ids.includes(perm.perm_id)}
                              onChange={() => togglePermission(perm.perm_id)}
                            />
                            <span className={styles.checkbox}>
                              {formData.permission_ids.includes(perm.perm_id) && (
                                <Check size={12} />
                              )}
                            </span>
                            <div className={styles.permInfo}>
                              <span className={styles.permName}>{perm.perm_name}</span>
                              {perm.perm_desc && (
                                <span className={styles.permDesc}>{perm.perm_desc}</span>
                              )}
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className={styles.modalFooter}>
                <button
                  className={styles.cancelBtn}
                  onClick={() => setShowModal(false)}
                  disabled={saving}
                >
                  取消
                </button>
                <button
                  className={styles.saveBtn}
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? (
                    <>
                      <Loader2 className={styles.spin} size={16} />
                      <span>保存中...</span>
                    </>
                  ) : (
                    <span>保存</span>
                  )}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* 删除确认弹窗 */}
      <AnimatePresence>
        {showDeleteConfirm && deletingRole && (
          <motion.div
            className={styles.modalOverlay}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowDeleteConfirm(false)}
          >
            <motion.div
              className={styles.confirmModal}
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className={styles.confirmIcon}>
                <Trash2 size={32} />
              </div>
              <h3 className={styles.confirmTitle}>确认删除</h3>
              <p className={styles.confirmText}>
                确定要删除角色「<strong>{deletingRole.role_name}</strong>」吗？
              </p>
              {deletingRole.user_count > 0 && (
                <p className={styles.confirmWarning}>
                  该角色下还有 {deletingRole.user_count} 个用户，无法删除
                </p>
              )}
              <div className={styles.confirmActions}>
                <button
                  className={styles.cancelBtn}
                  onClick={() => setShowDeleteConfirm(false)}
                >
                  取消
                </button>
                <button
                  className={styles.deleteBtn}
                  onClick={handleDeleteConfirm}
                  disabled={deletingRole.user_count > 0}
                >
                  确认删除
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}